// Touch command grammar (ui-map-interface.md §8). Every command on the map
// used to be a right-click, and iOS never sends `contextmenu` over a canvas,
// so a finger could look at the sea and nothing else. This module holds the
// two pure parts of the answer: the press state machine that tells a tap
// from a drag, and the decision table that says what a tap means.
//
// Nothing here touches the DOM, the canvas or a clock: the caller passes the
// timestamps in and adapts the decision to its own objects. That keeps the
// grammar pinned by touch.test.ts rather than by a browser.
//
// The caller must pass the EVENT'S own clock (`e.timeStamp`), never the
// clock read inside the handler (R583). A long frame delays the handler,
// and a handler-time clock then reads a short tap as a long rest.
//
// `ListTap` at the foot is the third pure part, and it serves the DOM
// chrome rather than the map: it tells a tap from a scroll inside a list
// that scrolls under the same finger (R573). `TitleHold` is the fourth: it
// tells a press on the chrome from a still finger that asks for a title
// (R1555).

import { ShipClass } from '../gen/proto/aquaphobia';

/** Tuning for the press state machine, in CSS pixels and milliseconds. */
export interface TapOptions {
  /** Travel that voids a press. A finger is never still. */
  slopPx: number;
  /** How long a still finger must rest before the press becomes a peek. */
  longPressMs: number;
}

export const TAP_DEFAULTS: TapOptions = { slopPx: 10, longPressMs: 450 };

/** True when (`x`, `y`) stands more than `slopPx` from where the press
 *  came down: the press is a drag now. */
function pastSlop(p: { x: number; y: number }, x: number, y: number, slopPx: number): boolean {
  return Math.hypot(x - p.x, y - p.y) > slopPx;
}

interface Press {
  id: number;
  x: number;
  y: number;
  t: number;
  /** Set once the long press has fired: that press can no longer tap. */
  fired: boolean;
}

/**
 * Turns raw pointer events into two gestures: a tap and a long press.
 *
 * The rules a finger needs, and the reasons they exist:
 *
 * - A tap needs exactly one pointer for the whole life of the press. A
 *   second finger poisons the press until every finger lifts, so the end of
 *   a pinch can never be read as a tap on whatever was under one thumb.
 * - Travel past the slop voids the press. The camera pans on the same
 *   canvas, so a pan must not also order a fleet.
 * - A press that rests past `longPressMs` is a peek, not a tap, and it fires
 *   once. `tick` fires it while the finger is still down (drive it from a
 *   timer); `up` fires it lazily if no timer ran. The lift reads the
 *   press's own two timestamps, so a press the browser reported late is
 *   still a tap even after the timer fired the peek (R583).
 * - The gesture lands where the finger came down, never where it lifted, so
 *   the slop cannot move an order.
 */
export class TapRecognizer {
  onTap: (x: number, y: number) => void = () => {};
  onLongPress: (x: number, y: number) => void = () => {};

  private press: Press | null = null;
  /** Every pointer id currently down on the surface. */
  private active = new Set<number>();

  constructor(readonly opts: TapOptions = TAP_DEFAULTS) {}

  down(pointerId: number, x: number, y: number, ts: number) {
    if (this.active.has(pointerId)) return; // a repeat down is not a new press
    this.active.add(pointerId);
    if (this.active.size > 1) {
      this.press = null;
      return;
    }
    this.press = { id: pointerId, x, y, t: ts, fired: false };
  }

  move(pointerId: number, x: number, y: number, ts: number) {
    const p = this.press;
    if (!p || p.id !== pointerId) return;
    if (pastSlop(p, x, y, this.opts.slopPx)) {
      this.press = null; // a drag: no tap, and no peek either
      return;
    }
    this.tick(ts);
  }

  up(pointerId: number, x: number, y: number, ts: number) {
    if (!this.active.delete(pointerId)) return; // never saw it come down
    const p = this.press;
    if (!p || p.id !== pointerId) return;
    this.press = null;
    if (pastSlop(p, x, y, this.opts.slopPx)) return;
    // The PRESS'S OWN two timestamps decide, and nothing else (R583). A
    // long frame can hold a `pointerup` back, or let the peek timer run
    // ahead of one already queued; either way the press itself was short,
    // and a short press is a tap. The peek the timer pinned is therefore
    // wrong, and the caller takes it down again on the tap.
    if (ts - p.t < this.opts.longPressMs) { this.onTap(p.x, p.y); return; }
    if (p.fired) return; // the peek consumed this press
    // Rested long enough, but no timer ran: the peek still owns it.
    this.onLongPress(p.x, p.y);
  }

  /** The browser took the pointer away (a scroll, a system gesture). */
  cancel(pointerId: number) {
    if (!this.active.delete(pointerId)) return;
    this.press = null;
  }

  /**
   * Forgets every finger the recognizer thinks is down.
   *
   * A page that goes to the background never gets the `pointerup`, so the
   * id stays in `active` for good: every later press then counts as a
   * second finger, poisons itself and taps stop working until the page is
   * reloaded. The caller is the `visibilitychange` handler, on hide.
   *
   * It fires nothing. A press interrupted by the player leaving the page
   * is not a tap and not a peek.
   */
  reset() {
    this.active.clear();
    this.press = null;
  }

  /** Fires the long press once the finger has rested long enough. */
  tick(ts: number) {
    const p = this.press;
    if (!p || p.fired) return;
    if (ts - p.t < this.opts.longPressMs) return;
    p.fired = true;
    this.onLongPress(p.x, p.y);
  }
}

// --- the tap grammar ---

/** What a tap landed on, in the only vocabulary the decision needs. */
export type TapTarget =
  | { kind: 'fleet'; own: boolean }
  /** A gather field: a deposit, or a vent of the survey (R3147). */
  | { kind: 'deposit' }
  | { kind: 'ownBase' }
  | { kind: 'site' }
  /** A point of interest of the survey (R3138): it selects as a site
   *  does. */
  | { kind: 'point' }
  | { kind: 'water' };

/** The selection a tap acts against. */
export interface TapContext {
  /** True when the selection is one of this commander's own fleets. */
  ownFleetSelected: boolean;
  /** True when that fleet is a Dredger — the hull whose order on a deposit
   * is a gather rather than a move (R80). */
  dredgerSelected: boolean;
}

/** The tap context of the selected own fleet, or of none. Both surfaces
 *  that read a tap build it here: the season map and the arcade match. */
export function tapContext(mine: { cls: ShipClass } | null | undefined): TapContext {
  return { ownFleetSelected: !!mine, dredgerSelected: mine?.cls === ShipClass.SHIP_CLASS_DREDGER };
}

/**
 * What a tap does.
 *
 * - `select` — take the tapped object as the selection.
 * - `command` — hand the point to the context command, the same body a
 *   right-click runs: attack-move, gather, return home, or move.
 * - `deselect` — drop the selection.
 */
export type TapDecision = 'select' | 'command' | 'deselect';

/**
 * The touch grammar. A finger has one button, so selection and command
 * share it: with nothing of yours selected a tap only ever looks, and with
 * one of your fleets selected a tap on anything that is not yours is an
 * order to it. Tapping another own fleet still switches the selection —
 * you can never trap yourself into being unable to pick a different hull.
 *
 * The DPS gate on an attack-move (R79) and the deposit snap (R80) belong to
 * the context command, not here: this returns `command` and lets the one
 * body that the mouse also runs decide the mission.
 */
export function resolveTap(target: TapTarget, ctx: TapContext): TapDecision {
  if (!ctx.ownFleetSelected) {
    return target.kind === 'water' ? 'deselect' : 'select';
  }
  switch (target.kind) {
    case 'fleet': return target.own ? 'select' : 'command';
    case 'deposit': return ctx.dredgerSelected ? 'command' : 'select';
    case 'ownBase': return 'command';
    case 'site':
    case 'point': return 'select';
    case 'water': return 'command';
  }
}

// --- a press inside a scrolling list ---------------------------------------

/** One press the list has not decided about yet. */
interface ListPress<T> {
  id: number;
  x: number;
  y: number;
  /** What the press came down on. The release acts on this and never on
   *  what the finger lifted over. */
  target: T;
}

/**
 * Tells a tap from a scroll inside a list that scrolls (R573).
 *
 * A DOM chrome acts on `pointerdown`, because a control destroyed between
 * the press and the release swallows the click. That rule is right where
 * nothing under the control scrolls. It is wrong inside a scroller: a
 * finger that starts a pan on a row presses that row at once.
 *
 * The rules a finger needs here:
 *
 * - The press remembers what it came down on, so the release acts on that
 *   element and never on the one the finger drifted over.
 * - Travel past the slop voids the press. The list pans under the same
 *   finger, so a pan must not also press a row.
 * - `cancel` voids the press. That is what the browser sends when it takes
 *   the touch for a pan of its own.
 * - A second finger voids the press. A pinch that ends over a row is not a
 *   press on it.
 *
 * There is no long press and no clock: a list row has one meaning. The type
 * of the target is the caller's, so nothing here touches the DOM.
 */
export class ListTap<T> {
  onTap: (target: T) => void = () => {};

  private press: ListPress<T> | null = null;

  constructor(readonly slopPx: number = TAP_DEFAULTS.slopPx) {}

  /** A press came down on `target`, or on nothing when it is null. */
  down(pointerId: number, x: number, y: number, target: T | null): void {
    if (this.press !== null && this.press.id !== pointerId) {
      this.press = null; // a second finger: this is a pinch, not a press
      return;
    }
    this.press = target === null ? null : { id: pointerId, x, y, target };
  }

  move(pointerId: number, x: number, y: number): void {
    const p = this.press;
    if (p === null || p.id !== pointerId) return;
    if (pastSlop(p, x, y, this.slopPx)) this.press = null;
  }

  /** The finger lifted. It taps while the press is still valid. */
  up(pointerId: number, x: number, y: number): void {
    const p = this.press;
    if (p === null || p.id !== pointerId) return;
    this.press = null;
    if (pastSlop(p, x, y, this.slopPx)) return;
    this.onTap(p.target);
  }

  /** The browser took the pointer away, or the page is going. */
  cancel(): void {
    this.press = null;
  }
}

// --- a title held on the chrome --------------------------------------------

/** One press that may become a title hold. */
interface HoldPress<T> {
  id: number;
  x: number;
  y: number;
  /** The event clock of the press (R583). */
  t: number;
  /** What the hold shows. It is read at the press, because a re-render
   *  can take the element away before the hold fires. */
  target: T;
}

/**
 * Tells a title hold from a press on the DOM chrome (R1555).
 *
 * A finger has no hover, so a `title` said nothing on a telephone. A still
 * finger that rests `holdMs` on an element with a title shows the title.
 * The rules a finger needs here:
 *
 * - The hold needs exactly one pointer. A second finger voids the press
 *   until every finger lifts, so a pinch never shows a title.
 * - Travel past the slop voids the press: the hosts that scroll pan under
 *   the same finger.
 * - `cancel` voids the press. That is what the browser sends when it takes
 *   the touch for a pan of its own.
 * - The hold fires once. `tick` fires it while the finger is still down
 *   (drive it from a timer); `up` fires it lazily when the press's own two
 *   timestamps say it rested long enough and no timer ran (R583).
 * - `held` stays true from the hold until the next press comes down, so
 *   the release of a held press can refuse to act as a tap.
 *
 * Nothing here touches the DOM or reads a clock. No pointer id is a sign
 * of "no pointer": an iPhone sends negative ids (R1494).
 */
export class TitleHold<T> {
  onHold: (target: T, x: number, y: number) => void = () => {};
  /** The last press became a hold, and no press came down since. */
  held = false;

  private press: HoldPress<T> | null = null;
  /** Every pointer id currently down on the chrome. */
  private active = new Set<number>();

  constructor(readonly holdMs: number, readonly slopPx: number = TAP_DEFAULTS.slopPx) {}

  /** A press came down. `target` is null when it holds no title. */
  down(pointerId: number, x: number, y: number, ts: number, target: T | null): void {
    if (this.active.has(pointerId)) return; // a repeat down is not a new press
    this.active.add(pointerId);
    this.held = false;
    this.press = this.active.size > 1 || target === null
      ? null
      : { id: pointerId, x, y, t: ts, target };
  }

  move(pointerId: number, x: number, y: number): void {
    const p = this.press;
    if (p === null || p.id !== pointerId) return;
    if (pastSlop(p, x, y, this.slopPx)) this.press = null;
  }

  /** The finger lifted. A press that rested long enough holds now. */
  up(pointerId: number, ts: number): void {
    this.active.delete(pointerId);
    const p = this.press;
    if (p === null || p.id !== pointerId) return;
    if (ts - p.t >= this.holdMs) this.fire();
    this.press = null;
  }

  /** The browser took the pointer away. */
  cancel(pointerId: number): void {
    this.active.delete(pointerId);
    this.press = null;
  }

  /** Forgets every finger: a hidden page never hears the lift. */
  reset(): void {
    this.active.clear();
    this.press = null;
  }

  /** Fires the hold once the finger has rested long enough. */
  tick(ts: number): void {
    const p = this.press;
    if (p !== null && ts - p.t >= this.holdMs) this.fire();
  }

  /** Fires the waiting press now. The platform's own long press, the
   *  `contextmenu` of an Android browser, calls it. */
  fire(): void {
    const p = this.press;
    if (p === null) return;
    this.press = null;
    this.held = true;
    this.onHold(p.target, p.x, p.y);
  }

  /** A press waits to become a hold. */
  get pending(): boolean {
    return this.press !== null;
  }

  /** The ms until the waiting press holds, at the clock `ts`, or null. */
  dueIn(ts: number): number | null {
    const p = this.press;
    return p === null ? null : Math.max(0, p.t + this.holdMs - ts);
  }
}
